import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  SwapVert as SwapVertIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import {
  inventoryService,
  StockItem,
  StockMovement,
  Warehouse,
  StockAdjustment,
} from '../../services/inventoryService';
import InventoryControlTower from './InventoryControlTower';
import InventoryCommandCenter from './InventoryCommandCenter';
import ItemWorkspace360Page from './ItemWorkspace360Page';
import WarehouseCommandPage from './WarehouseCommandPage';
import ReorderPlanningPage from './ReorderPlanningPage';
import ExpiryDashboardPage from './ExpiryDashboardPage';
import InventoryAnalyticsPage from './InventoryAnalyticsPage';
import SerialNumbersPage from './SerialNumbersPage';

// Utility function to format currency
const formatCurrency = (value: number): string => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Utility function to get stock status
const getStockStatus = (item: StockItem): { label: string; color: 'success' | 'warning' | 'error' } => {
  if (item.currentStock === 0) {
    return { label: 'Out of Stock', color: 'error' };
  } else if (item.currentStock <= item.reorderLevel) {
    return { label: 'Low Stock', color: 'warning' };
  }
  return { label: 'Normal', color: 'success' };
};

// Utility function to get movement type config
const getMovementTypeConfig = (type: string): { color: 'success' | 'error' | 'info'; icon: React.ReactElement } => {
  switch (type) {
    case 'IN':
      return { color: 'success', icon: <TrendingUpIcon fontSize="small" /> };
    case 'OUT':
      return { color: 'error', icon: <TrendingDownIcon fontSize="small" /> };
    case 'ADJUSTMENT':
      return { color: 'info', icon: <SwapVertIcon fontSize="small" /> };
    default:
      return { color: 'info', icon: <SwapVertIcon fontSize="small" /> };
  }
};

// StockListPage Component
const StockListPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  // Dialogs
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [levelsDialogOpen, setLevelsDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);

  // Adjust Stock Form
  const [adjustmentForm, setAdjustmentForm] = useState({
    movementType: 'IN' as 'IN' | 'OUT' | 'ADJUSTMENT',
    quantity: '',
    reason: '',
    unitCost: '',
  });

  // Update Levels Form
  const [levelsForm, setLevelsForm] = useState({
    minimumStock: '',
    maximumStock: '',
    reorderLevel: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryService.getStock();
      setStockItems(response.stock || []);

      // Extract unique categories
      const uniqueCategories = Array.from(new Set(response.stock?.map((item: StockItem) => item.categoryName) || []));
      setCategories(uniqueCategories);
    } catch (err: any) {
      console.error('Failed to fetch stock:', err);
      setError(err.message || 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = (item: StockItem) => {
    setSelectedStock(item);
    setAdjustmentForm({
      movementType: 'IN',
      quantity: '',
      reason: '',
      unitCost: item.averageCost.toString(),
    });
    setAdjustDialogOpen(true);
  };

  const handleUpdateLevels = (item: StockItem) => {
    setSelectedStock(item);
    setLevelsForm({
      minimumStock: item.minimumStock.toString(),
      maximumStock: item.maximumStock.toString(),
      reorderLevel: item.reorderLevel.toString(),
    });
    setLevelsDialogOpen(true);
  };

  const submitAdjustment = async () => {
    if (!selectedStock) return;

    try {
      const adjustment: StockAdjustment = {
        productId: selectedStock.productId,
        warehouseId: selectedStock.warehouseId,
        movementType: adjustmentForm.movementType,
        quantity: parseFloat(adjustmentForm.quantity),
        reason: adjustmentForm.reason,
        unitCost: parseFloat(adjustmentForm.unitCost) || undefined,
      };

      await inventoryService.adjustStock(adjustment);
      setSnackbar({ open: true, message: 'Stock adjusted successfully', severity: 'success' });
      setAdjustDialogOpen(false);
      fetchStock();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to adjust stock', severity: 'error' });
    }
  };

  const submitLevelsUpdate = async () => {
    if (!selectedStock) return;

    try {
      await inventoryService.updateStockLevels(selectedStock.id, {
        minimumStock: parseFloat(levelsForm.minimumStock),
        maximumStock: parseFloat(levelsForm.maximumStock),
        reorderLevel: parseFloat(levelsForm.reorderLevel),
      });
      setSnackbar({ open: true, message: 'Stock levels updated successfully', severity: 'success' });
      setLevelsDialogOpen(false);
      fetchStock();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to update levels', severity: 'error' });
    }
  };

  // Filter stock items
  const filteredStock = stockItems.filter((item) => {
    const matchesSearch =
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productCode.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || item.categoryName === categoryFilter;

    const matchesStatus = !statusFilter ||
      (statusFilter === 'low' && item.currentStock <= item.reorderLevel && item.currentStock > 0) ||
      (statusFilter === 'out' && item.currentStock === 0) ||
      (statusFilter === 'normal' && item.currentStock > item.reorderLevel);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Stock Management
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        View and manage inventory stock levels across all warehouses
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search by product name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Stock Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Stock Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low Stock</MenuItem>
                  <MenuItem value="out">Out of Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchStock}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stock Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product Code</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">Min/Max</TableCell>
                  <TableCell align="right">Reorder Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Avg Cost</TableCell>
                  <TableCell align="right">Stock Value</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No stock items found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStock.map((item) => {
                    const status = getStockStatus(item);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.productCode}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.categoryName}</TableCell>
                        <TableCell>{item.warehouseName}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={status.color}
                            fontWeight="medium"
                          >
                            {item.currentStock} {item.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontSize="0.75rem">
                            {item.minimumStock} / {item.maximumStock} {item.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.reorderLevel} {item.unit}
                        </TableCell>
                        <TableCell>
                          <Chip label={status.label} color={status.color} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.averageCost)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(item.stockValue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Adjust Stock">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleAdjustStock(item)}
                            >
                              <SwapVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Update Levels">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleUpdateLevels(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Adjust Stock Dialog */}
      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Adjust Stock - {selectedStock?.productName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Current Stock: {selectedStock?.currentStock} {selectedStock?.unit} at {selectedStock?.warehouseName}
                </Alert>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Movement Type</InputLabel>
                  <Select
                    value={adjustmentForm.movementType}
                    label="Movement Type"
                    onChange={(e) =>
                      setAdjustmentForm({ ...adjustmentForm, movementType: e.target.value as any })
                    }
                  >
                    <MenuItem value="IN">Stock In (Increase)</MenuItem>
                    <MenuItem value="OUT">Stock Out (Decrease)</MenuItem>
                    <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={adjustmentForm.quantity}
                  onChange={(e) =>
                    setAdjustmentForm({ ...adjustmentForm, quantity: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{selectedStock?.unit}</InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Cost"
                  type="number"
                  value={adjustmentForm.unitCost}
                  onChange={(e) =>
                    setAdjustmentForm({ ...adjustmentForm, unitCost: e.target.value })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Reason / Remarks"
                  multiline
                  rows={3}
                  value={adjustmentForm.reason}
                  onChange={(e) =>
                    setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })
                  }
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdjustment}
            disabled={!adjustmentForm.quantity || !adjustmentForm.reason}
          >
            Submit Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Levels Dialog */}
      <Dialog open={levelsDialogOpen} onClose={() => setLevelsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Stock Levels - {selectedStock?.productName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Current Stock: {selectedStock?.currentStock} {selectedStock?.unit}
                </Alert>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Minimum Stock"
                  type="number"
                  value={levelsForm.minimumStock}
                  onChange={(e) =>
                    setLevelsForm({ ...levelsForm, minimumStock: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{selectedStock?.unit}</InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Maximum Stock"
                  type="number"
                  value={levelsForm.maximumStock}
                  onChange={(e) =>
                    setLevelsForm({ ...levelsForm, maximumStock: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{selectedStock?.unit}</InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Reorder Level"
                  type="number"
                  value={levelsForm.reorderLevel}
                  onChange={(e) =>
                    setLevelsForm({ ...levelsForm, reorderLevel: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{selectedStock?.unit}</InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLevelsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitLevelsUpdate}
            disabled={
              !levelsForm.minimumStock ||
              !levelsForm.maximumStock ||
              !levelsForm.reorderLevel
            }
          >
            Update Levels
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// StockMovementsPage Component
const StockMovementsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [movementTypeFilter, setMovementTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchMovements();
  }, []);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (movementTypeFilter) params.movementType = movementTypeFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await inventoryService.getMovements(params);
      setMovements(response.movements || []);
    } catch (err: any) {
      console.error('Failed to fetch movements:', err);
      setError(err.message || 'Failed to load stock movements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
  }, [movementTypeFilter, startDate, endDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Stock Movements
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Track all inventory movements including stock in, stock out, and adjustments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Movement Type</InputLabel>
                <Select
                  value={movementTypeFilter}
                  label="Movement Type"
                  onChange={(e) => setMovementTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="IN">Stock In</MenuItem>
                  <MenuItem value="OUT">Stock Out</MenuItem>
                  <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchMovements}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Movements Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product Code</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No stock movements found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement) => {
                    const typeConfig = getMovementTypeConfig(movement.movementType);
                    const totalValue = movement.quantity * movement.unitCost;
                    return (
                      <TableRow key={movement.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem">
                            {new Date(movement.movementDate).toLocaleDateString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(movement.movementDate).toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>{movement.productCode}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {movement.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>{movement.warehouseName}</TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={typeConfig.icon}
                            label={movement.movementType}
                            color={typeConfig.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color={typeConfig.color}
                          >
                            {movement.movementType === 'OUT' ? '-' : '+'}
                            {movement.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(movement.unitCost)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(totalValue)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {movement.referenceType && (
                            <Typography variant="body2" fontSize="0.75rem">
                              {movement.referenceType}
                              {movement.referenceId && ` #${movement.referenceId}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem">
                            {movement.remarks || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem">
                            {movement.createdBy}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

// WarehousesPage Component
const WarehousesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  const [warehouseForm, setWarehouseForm] = useState({
    warehouseCode: '',
    warehouseName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    isActive: true,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryService.getWarehouses();
      setWarehouses(response.warehouses || []);
    } catch (err: any) {
      console.error('Failed to fetch warehouses:', err);
      setError(err.message || 'Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedWarehouse(null);
    setWarehouseForm({
      warehouseCode: '',
      warehouseName: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setEditMode(true);
    setSelectedWarehouse(warehouse);
    setWarehouseForm({
      warehouseCode: warehouse.warehouseCode,
      warehouseName: warehouse.warehouseName,
      address: warehouse.address,
      city: warehouse.city,
      state: warehouse.state,
      pincode: warehouse.pincode,
      isActive: warehouse.isActive,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode && selectedWarehouse) {
        await inventoryService.updateWarehouse(selectedWarehouse.id, warehouseForm);
        setSnackbar({ open: true, message: 'Warehouse updated successfully', severity: 'success' });
      } else {
        await inventoryService.createWarehouse(warehouseForm);
        setSnackbar({ open: true, message: 'Warehouse created successfully', severity: 'success' });
      }
      setDialogOpen(false);
      fetchWarehouses();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to save warehouse', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Warehouse Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage warehouse locations and storage facilities
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Warehouse
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Warehouses Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Warehouse Code</TableCell>
                  <TableCell>Warehouse Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Pincode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No warehouses found. Click "Add Warehouse" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  warehouses.map((warehouse) => (
                    <TableRow key={warehouse.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {warehouse.warehouseCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <WarehouseIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            {warehouse.warehouseName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {warehouse.address}
                        </Typography>
                      </TableCell>
                      <TableCell>{warehouse.city}</TableCell>
                      <TableCell>{warehouse.state}</TableCell>
                      <TableCell>{warehouse.pincode}</TableCell>
                      <TableCell>
                        <Chip
                          label={warehouse.isActive ? 'Active' : 'Inactive'}
                          color={warehouse.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {new Date(warehouse.createdAt).toLocaleDateString('en-IN')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Warehouse">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(warehouse)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Warehouse Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Warehouse' : 'Add New Warehouse'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Warehouse Code"
                  value={warehouseForm.warehouseCode}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, warehouseCode: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Warehouse Name"
                  value={warehouseForm.warehouseName}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, warehouseName: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={warehouseForm.address}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, address: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={warehouseForm.city}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, city: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={warehouseForm.state}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, state: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={warehouseForm.pincode}
                  onChange={(e) =>
                    setWarehouseForm({ ...warehouseForm, pincode: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={warehouseForm.isActive ? 'active' : 'inactive'}
                    label="Status"
                    onChange={(e) =>
                      setWarehouseForm({ ...warehouseForm, isActive: e.target.value === 'active' })
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !warehouseForm.warehouseCode ||
              !warehouseForm.warehouseName ||
              !warehouseForm.address ||
              !warehouseForm.city ||
              !warehouseForm.state ||
              !warehouseForm.pincode
            }
          >
            {editMode ? 'Update' : 'Create'} Warehouse
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Main InventoryPage Component with Routes
const InventoryPage: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Routes>
        <Route index element={<InventoryCommandCenter />} />
        <Route path="overview" element={<InventoryControlTower />} />
        <Route path="stock" element={<StockListPage />} />
        <Route path="movements" element={<StockMovementsPage />} />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="item/:id" element={<ItemWorkspace360Page />} />
        <Route path="warehouse/:id" element={<WarehouseCommandPage />} />
        <Route path="reorder" element={<ReorderPlanningPage />} />
        <Route path="expiry" element={<ExpiryDashboardPage />} />
        <Route path="analytics" element={<InventoryAnalyticsPage />} />
        <Route path="serials" element={<SerialNumbersPage />} />
      </Routes>
    </Box>
  );
};

export default InventoryPage;
